import { useState } from "react";
import { X, Upload, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function PmaxCsvModal({ jobId, pmaxContext, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const accountCurrency = pmaxContext?.currency_code;
  const apiTermCount = pmaxContext?.api_term_count ?? 0;
  const spendingCount = pmaxContext?.spending_campaign_count ?? 0;
  const dateFrom = pmaxContext?.date_from;
  const dateTo = pmaxContext?.date_to;
  const dateRangeLabel = dateFrom && dateTo ? `${formatDate(dateFrom)} – ${formatDate(dateTo)}` : null;

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f);
    setPreview(null);
    setPreviewing(true);
    try {
      const p = await api.previewPmaxCsv(jobId, f);
      setPreview(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't parse CSV.");
      setFile(null);
    } finally {
      setPreviewing(false);
    }
  };

  const confirm = async () => {
    if (!file) return;
    if (preview?.currency_mismatch) {
      toast.error("Currency mismatch — cannot upload.");
      return;
    }
    setSubmitting(true);
    try {
      await api.submitPmaxCsv(jobId, { file, skip: false });
      toast.success("CSV merged. Continuing briefing…");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
      setSubmitting(false);
    }
  };

  const skip = async () => {
    setSubmitting(true);
    try {
      await api.submitPmaxCsv(jobId, { skip: true });
      toast.success("Continuing without CSV.");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't continue.");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">optional enrichment</p>
            <h2 className="mt-1 font-heading text-xl font-medium text-slate-900">
              Add PMAX search terms from Google Ads UI
            </h2>
          </div>
          <button onClick={skip} disabled={submitting} className="text-slate-400 hover:text-slate-700">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <p className="font-medium">Why upload this?</p>
            <p className="mt-1 text-xs leading-relaxed">
              Google's API only returns roughly the last 30–90 days of PMAX search terms.
              We already pulled <span className="num font-semibold">{apiTermCount}</span> terms
              across <span className="num font-semibold">{spendingCount}</span> spending PMAX campaign(s).
              If you have older data from Google Ads UI, export it and drop it below —
              we'll merge it in without duplicating anything the API already returned.
            </p>
            {dateRangeLabel && (
              <p className="mt-2 rounded bg-white/60 px-2 py-1.5 text-xs">
                <span className="font-semibold">Export this date range from Ads UI:</span>{" "}
                <span className="num font-semibold">{dateRangeLabel}</span>{" "}
                <span className="text-amber-700">
                  (matches this briefing's lookback of {pmaxContext?.lookback_days} days).
                  Overlap with what we already pulled will be deduped automatically.
                </span>
              </p>
            )}
            <p className="mt-2 text-xs">
              <span className="font-medium">How to export:</span> Google Ads → Insights & reports →
              Search terms → set the date range above → download CSV. Account currency:
              <span className="num ml-1 font-semibold">{accountCurrency || "unknown"}</span>.
            </p>
          </div>

          <label className="mt-5 block cursor-pointer">
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              disabled={submitting}
            />
            <div className="flex items-center gap-3 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center transition-all hover:border-slate-300">
              <Upload size={18} className="text-slate-400" />
              <span className="text-sm text-slate-600">
                {file ? file.name : "Click to select a CSV, or drop it here"}
              </span>
            </div>
          </label>

          {previewing && (
            <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 size={14} className="animate-spin" /> Parsing CSV…
            </div>
          )}

          {preview && !previewing && (
            <div className="mt-5 space-y-3">
              <div className="rounded-md border border-slate-200 bg-white p-4 text-sm">
                <p className="font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Detected
                </p>
                <ul className="mt-2 space-y-1 text-xs text-slate-700">
                  <li><span className="text-slate-500">Date range:</span> {preview.date_range || "not stated"}</li>
                  <li><span className="text-slate-500">Currency:</span> {preview.currency || "not detected"}</li>
                  <li><span className="text-slate-500">Rows parsed:</span> {preview.row_count}</li>
                </ul>
              </div>

              {preview.currency_mismatch && (
                <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
                  <div className="flex items-start gap-2">
                    <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Currency mismatch</p>
                      <p className="mt-1">
                        Account is <span className="font-semibold">{preview.account_currency}</span>,
                        but CSV shows <span className="font-semibold">{preview.currency}</span>.
                        Re-export from Ads UI matching the account currency.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-md border border-slate-200 bg-white p-4">
                <p className="font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Column mapping
                </p>
                <table className="mt-2 w-full text-xs">
                  <tbody>
                    {(preview.mapping || []).map((m, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="py-1.5 text-slate-500">{m.csv_column}</td>
                        <td className="py-1.5 text-slate-400">→</td>
                        <td className="py-1.5 font-medium text-slate-900">{m.internal_field}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(preview.dropped_columns || []).length > 0 && (
                  <p className="mt-3 text-[11px] text-slate-400">
                    Dropped (computed / not used): {preview.dropped_columns.join(", ")}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4">
          <button
            onClick={skip}
            disabled={submitting}
            className="text-sm text-slate-500 hover:text-slate-900 disabled:opacity-50"
          >
            Skip — continue without CSV
          </button>
          <button
            onClick={confirm}
            disabled={!preview || preview.currency_mismatch || submitting}
            className="inline-flex items-center gap-2 rounded-md bg-[#0052FF] px-5 py-2 text-sm font-semibold text-white hover:bg-[#0040CC] disabled:opacity-40"
          >
            {submitting ? (
              <><Loader2 size={14} className="animate-spin" /> Merging…</>
            ) : (
              <><CheckCircle2 size={14} /> Confirm & continue</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
